import { formatScore } from './scoreFormat';

export function getResultIdentity(result) {
  if (result.testId) {
    return `id:${result.testId}`;
  }

  if (result.testSnapshot?.originalTestId) {
    return `id:${result.testSnapshot.originalTestId}`;
  }

  if (result.testSnapshot?.title) {
    const snapshotCategoryKey =
      result.testSnapshot.categoryKey || result.testSnapshot.category || '';
    return `snapshot:${result.testSnapshot.title}:${snapshotCategoryKey}`;
  }

  return null;
}

export function buildProgressTimeline(results = []) {
  const previousByIdentity = new Map();

  return [...results]
    .filter((result) => result && result.date)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map((result) => {
      const identity = getResultIdentity(result);
      const previous = identity ? previousByIdentity.get(identity) : null;
      const score = Number(result.score) || 0;
      const previousScore = previous ? Number(previous.score) || 0 : null;
      const point = {
        id: result._id,
        title: result.testSnapshot?.title || 'Неизвестный тест',
        date: result.date,
        score,
        formattedScore: formatScore(score),
        delta: previous ? score - previousScore : null,
      };

      if (identity) {
        previousByIdentity.set(identity, result);
      }

      return point;
    });
}

export function getComparisonSummary(comparison) {
  if (!comparison) {
    return null;
  }

  const delta = Number(comparison.scoreDelta) || 0;
  const absoluteDelta = Math.abs(delta);

  if (comparison.direction === 'same' || delta === 0) {
    return {
      tone: 'neutral',
      title: 'Без изменений',
      description: `Результат совпадает с прошлым прохождением: ${formatScore(comparison.currentScore)}.`,
    };
  }

  if (comparison.direction === 'lower') {
    return {
      tone: 'success',
      title: 'Лучше, чем в прошлый раз',
      description: `Балл снизился на ${formatScore(absoluteDelta)}. Для PTSD-опросников снижение обычно означает уменьшение выраженности симптомов.`,
    };
  }

  return {
    tone: 'warning',
    title: 'Выше, чем в прошлый раз',
    description: `Балл вырос на ${formatScore(absoluteDelta)}. Это повод бережно пересмотреть нагрузку и поддержку в ближайшие дни.`,
  };
}

export function getRecommendedExercises(result = {}) {
  const levelText = `${result.interpretation?.level || ''} ${result.interpretation?.interpretation || ''}`.toLowerCase();
  const isElevated = ['высок', 'тяж', 'криз', 'остр'].some((word) => levelText.includes(word));

  const exercises = [
    {
      title: 'Дыхание 4-6',
      description: 'Вдыхайте на 4 счета и выдыхайте на 6 счетов в течение 3-5 минут, чтобы снизить физиологическое напряжение.',
    },
    {
      title: 'Заземление 5-4-3-2-1',
      description: 'Назовите 5 предметов вокруг, 4 звука или ощущения, 3 текстуры, 2 запаха и 1 действие, которое поможет вернуться в момент.',
    },
    {
      title: 'Короткий план поддержки',
      description: 'Запишите один безопасный контакт, одно успокаивающее действие и один шаг, который можно сделать сегодня.',
    },
  ];

  if (isElevated) {
    return [
      ...exercises,
      {
        title: 'Снижение нагрузки на 24 часа',
        description: 'Отложите необязательные стрессовые задачи и выберите спокойную рутину: сон, еда, вода, прогулка или разговор с близким.',
      },
    ];
  }

  return exercises;
}

export function getCrisisInformation() {
  return {
    title: 'Если сейчас небезопасно',
    description: 'Если есть риск причинить вред себе или другому человеку, немедленно обратитесь в местные экстренные службы или к ближайшему доверенному человеку. Этот сервис не заменяет врача, психотерапевта или кризисную помощь.',
    contacts: [
      '112 - единый номер экстренных служб',
      'Местная кризисная линия или дежурный врач',
      'Человек, которому вы доверяете и можете написать прямо сейчас',
    ],
  };
}

export function formatRetakeReminder(reminder) {
  if (!reminder) {
    return null;
  }

  return {
    title: reminder.title,
    message: `Последнее прохождение: ${new Date(reminder.lastDate).toLocaleDateString('ru-RU')}. Рекомендуем повторить (интервал ${reminder.intervalDays} дн., прошло ${reminder.daysSince} дн.).`,
    href: reminder.href,
    overdueDays: reminder.overdueDays,
  };
}

export function getPsychologistCommentAuthor(comment) {
  if (comment.psychologistName) {
    return comment.psychologistName;
  }
  const psychologist = comment.psychologistId;
  if (psychologist && typeof psychologist === 'object') {
    return psychologist.username
      || [psychologist.firstName, psychologist.lastName].filter(Boolean).join(' ')
      || 'Психолог';
  }
  return 'Психолог';
}
